import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Lazy-loaded blog code — kept out of the authenticated app bundle
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Invoices from "./pages/Invoices";
import CreateInvoice from "./pages/CreateInvoice";
import AdminMetrics from "./pages/AdminMetrics";
import EditInvoice from "./pages/EditInvoice";
import InvoiceDetail from "./pages/InvoiceDetail";
import RecurringInvoices from "./pages/RecurringInvoices";
import Reports from "./pages/Reports";
import Products from "./pages/Products";
import Marketing from "./pages/Marketing";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Support from "./pages/Support";
import Clients from "./pages/Clients";
import Settings from "./pages/Settings";
import PublicInvoice from "./pages/PublicInvoice";
import FreeInvoiceGenerator from "./pages/FreeInvoiceGenerator";
import Welcome from "./pages/Welcome";
import ChangeEmail from "./pages/ChangeEmail";
import Links from "./pages/Links";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import GA4RouteTracker from "./components/GA4RouteTracker";
import HeyCatchInitGate from "./components/HeyCatchInitGate";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// HeyCatch reserved short-link paths (/a-/z, /0-9) — explicit app-level
// redirect so it doesn't depend on the analytics bundle evaluating before
// React Router's own catch-all fires. The SDK's own runtime auto-forward
// (from init() in src/index.js) also covers this, but the doc says an
// explicit rule is "always preferred" since it doesn't depend on timing.
const SHORT_LINK_PATTERN = /^[a-z0-9]$/i;

const ShortLinkRedirect = () => {
  const { shortCode } = useParams();
  if (SHORT_LINK_PATTERN.test(shortCode || '')) {
    return <Navigate to={`/?utm_source=heycatch&utm_campaign=${shortCode}`} replace />;
  }
  return <Navigate to="/" replace />;
};

// Router with session_id check
const AppRouter = () => {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/pay/:invoiceId" element={<PublicInvoice />} />
      <Route path="/free-invoice-generator" element={<FreeInvoiceGenerator />} />
      <Route path="/invoice-template/photographers" element={<FreeInvoiceGenerator />} />
      <Route path="/invoice-template/musicians" element={<FreeInvoiceGenerator />} />
      <Route path="/invoice-template/designers" element={<FreeInvoiceGenerator />} />
      <Route path="/invoice-template/filmmakers" element={<FreeInvoiceGenerator />} />
      <Route path="/invoice-template/freelancers" element={<FreeInvoiceGenerator />} />

      {/* Blog — lazy-loaded so blog code is not bundled with the authenticated app */}
      <Route
        path="/blog"
        element={
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <BlogIndex />
          </Suspense>
        }
      />
      <Route
        path="/blog/:slug"
        element={
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <BlogPost />
          </Suspense>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminMetrics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <Welcome />
          </ProtectedRoute>
        }
      />
      <Route path="/change-email" element={<ChangeEmail />} />
      <Route path="/links" element={<Links />} />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <Invoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/new"
        element={
          <ProtectedRoute>
            <CreateInvoice />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:invoiceId/edit"
        element={
          <ProtectedRoute>
            <EditInvoice />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:invoiceId"
        element={
          <ProtectedRoute>
            <InvoiceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring"
        element={
          <ProtectedRoute>
            <RecurringInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing"
        element={
          <ProtectedRoute>
            <Marketing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route path="/:shortCode" element={<ShortLinkRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <GA4RouteTracker />
          <HeyCatchInitGate />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
